<!-- Basically like a SpotDecoderEntry, but for a Ticker instead of a Spot.

Used as a child item grouped below a Spot.
-->

<template>
  <div>
    <v-list
      dense
      avatar
      class="pa-0"
    >
      <v-list-tile
        class="entry-values entry-tile mt-1 list-tile-align-start"
        :ripple="false"
      >
        <transition
          name="slide-x-transition"
          mode="out-in"
        >
          <div
            v-if="showSelectionCheckbox"
            style="margin-top: auto; margin-bottom: auto"
          >
            <v-checkbox
              v-model="entry.selected"
              color="msr"
              class="no-checkbox-margin-top"
              hide-details
            />
          </div>
        </transition>
        <v-list-tile-avatar class="mr-3 smaller-avatar clickable">
          <v-icon>tv</v-icon>
        </v-list-tile-avatar>
        <div class="entry-item-group grow-3">
          <div class="entry-item grow-3">
            <v-list-tile-title
              class="mt-2 clickable"
              :class="{'msr--text': showSelectionCheckbox && entry.selected}"
              @click="goToDecoder(entry.decoder)"
            >
              {{ entry.decoder.description }}
            </v-list-tile-title>
          </div>
          <div
            v-if="showTiming && entry.has_timing"
            class="entry-item grow-1 timing"
            :class="getTimingCSS(entry, showActions)"
            @click.stop="showActions ? (showEntryTimingDialog = true) : null"
          >
            <small>
              {{ entry.startdate | dayjs('DD.MM.YYYY') }}
              <span
                v-if="entry.stopdate !== null"
              >- {{ entry.stopdate | dayjs('DD.MM.YYYY') }}</span>
            </small>
          </div>
        </div>
        <!-- Actions Menu -->
        <div class="entry-item-group last mt-1">
          <v-icon
            v-if="entry.decoder.read_only"
            class="warning--text clickable"
            @click.stop="alertReadOnly()"
          >
            lock
          </v-icon>
          <v-layout
            v-if="showStatus"
            row
            justify-end
            align-center
          >
            <div v-if="entry.has_timing">
              <v-icon
                class="msr--text mt-1"
                :class="{clickable: showActions}"
                style="font-size: 1.25em"
                @click.stop="
                  showActions ? (showEntryTimingDialog = true) : false
                "
              >
                timer
              </v-icon>
            </div>
            <v-menu
              v-if="showActions"
              offset-y
              left
              @click.native.stop
            >
              <v-icon
                slot="activator"
                class="pa-1"
                :medium="$vuetify.breakpoint.xsOnly"
              >
                more_vert
              </v-icon>
              <v-list>
                <v-list-tile @click="showEntryTimingDialog = true">
                  <v-list-tile-title class="menu-item">
                    <v-icon
                      class="mr-2"
                      :class="{
                        'msr--text': entry.has_timing,
                        'grey--text': !entry.has_timing,
                      }"
                    >
                      timer
                    </v-icon>
                    {{ $t('timing') }}
                  </v-list-tile-title>
                </v-list-tile>
                <v-list-tile
                  :disabled="!removalAllowed"
                  @click="showRemoveTickerFromDecoderDialog = true"
                >
                  <v-list-tile-title class="menu-item">
                    <v-icon class="grey--text mr-2">
                      block
                    </v-icon>
                    {{ $t('deactivate') }}
                  </v-list-tile-title>
                </v-list-tile>
              </v-list>
            </v-menu>
          </v-layout>
        </div>
      </v-list-tile>
    </v-list>
    <!-- Entry Timing Dialog -->
    <entry-timing-dialog
      :show.sync="showEntryTimingDialog"
      :entries="[entry]"
      :show-timeframe-controls="false"
      @submitted="$emit('changed')"
    />
    <!-- Removal Dialog -->
    <v-dialog
      v-model="showRemoveTickerFromDecoderDialog"
      absolute
    >
      <v-card>
        <v-card-title>
          <div class="headline">
            {{ $t('askDeleteEntry') }}
          </div>
          <v-btn
            style="position: absolute; top: 0; right: 0"
            flat
            icon
            :title="$t('close')"
            @click="showRemoveTickerFromDecoderDialog = false"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-actions>
          <v-spacer />
          <btn-ok
            round
            @click="removeTickerFromDecoder(entry.ticker, entry.decoder)"
          />
          <v-spacer />
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-divider v-if="showDivider" />
  </div>
</template>

<script>
import commonTicker from '@/mixins/commonTicker.js';

import BtnOk from '@/components/ButtonOk';
import EntryTimingDialog from '@/components/EntryTimingDialog';

export default {
  components: {
    BtnOk,
    EntryTimingDialog,
  },
  mixins: [commonTicker],
  props: {
    entry: {
      type: Object,
      required: true,
    },
    /* Whether to enable the 'remove this entry' menu action. */
    removalAllowed: {
      type: Boolean,
      default: true,
    },
    /* All props below control column visibility. */
    showSelectionCheckbox: {
      type: Boolean,
      default: false,
    },
    showActions: {
      type: Boolean,
      default: true,
    },
    showTiming: {
      type: Boolean,
      default: true,
    },
    showStatus: {
      type: Boolean,
      default: true,
    },
    showDivider: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      showEntryTimingDialog: false,
    };
  },
  methods: {
    goToDecoder(decoder) {
      let path = '/player/' + decoder.ctype + '/' + decoder.id;
      this.$router.push(path);
    },
  },
};
</script>

<style>
/* Ensure full width and adaptive height for v-list-tile. */
.entry-tile .v-list__tile {
  width: 100%;
  height: auto !important;
}
</style>

<style scoped>
.back-btn {
  margin: 0;
  padding: 0;
}

.menu-item {
  display: flex;
  align-items: center;
}

.entry-headers {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  height: auto;
  padding: 0;
}

.entry-values {
  display: flex;
}

.entry-item {
  margin: 0.1em 0 0.1em 0;
}
.entry-item.last {
  margin-right: 0;
}

.entry-item-group {
  display: flex;
  flex-flow: row nowrap;
  align-items: flex-start;
}
.entry-item-group.last {
  justify-content: flex-end;
  align-items: flex-end;
}
@media (max-width: 959px) {
  .entry-item-group {
    flex-flow: column nowrap;
  }
  .entry-item-group.last {
    justify-content: flex-start;
    align-items: flex-end;
  }
}

.timing {
  display: flex;
  align-self: center;
}
@media (orientation: landscape) and (max-width: 599px) {
  .timing {
    margin-left: 1em;
  }
}

.category {
  min-width: 80px;
}
@media (max-width: 599px) {
  .category {
    margin-top: 0.5em;
  }
  .timing {
    align-self: auto !important;
  }
}

.spot-status {
  height: 55px;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
}
@media (max-width: 599px) {
  .spot-status {
    height: 75px;
  }
}

.decoder-subtitle {
  display: flex;
  justify-content: space-between;
}

.smaller-avatar {
  min-width: auto !important;
  width: auto !important;
  min-height: auto !important;
  height: auto !important;
  margin: 0.5em;
}
.smaller-avatar >>> .v-avatar {
  min-width: auto !important;
  width: auto !important;
  min-height: auto !important;
  height: auto !important;
  margin: 0.5em;
}

.no-checkbox-margin-top.v-input--selection-controls {
  margin-top: 0;
}
</style>



// WEBPACK FOOTER //
// src/components/TickerDecoderEntry.vue